import React from 'react';
import { Box, TextField } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * EditableTextField - A reusable text field component for edit mode in accordions
 *
 * This component wraps a multiline TextField with:
 * - Click propagation stopping (prevents accordion collapse when editing)
 * - Consistent styling across all SplitView components
 * - Customizable min rows and other TextField props
 */
const EditableTextField = ({
  value,
  onChange,
  minRows = 15,
  placeholder = '',
  disabled = false,
  autoFocus = false,
  sx = {},
  ...props
}) => {
  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <TextField
        multiline
        fullWidth
        value={value}
        onChange={onChange}
        variant="outlined"
        minRows={minRows}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        sx={{
          '& .MuiOutlinedInput-root': {
            fontSize: '14px',
            fontFamily: 'inherit',
            lineHeight: 1.6,
            '& fieldset': {
              borderWidth: '1px',
            },
            '&:hover fieldset': {
              borderWidth: '1px',
            },
            '&.Mui-focused fieldset': {
              borderWidth: '1px',
            },
          },
          '& .MuiOutlinedInput-input': {
            fontSize: '14px',
            fontFamily: 'inherit',
            lineHeight: 1.6,
          },
          ...sx,
        }}
        {...props}
      />
    </Box>
  );
};

EditableTextField.propTypes = {
  /** The current value of the text field */
  value: PropTypes.string.isRequired,
  /** Callback function when value changes - receives the event */
  onChange: PropTypes.func.isRequired,
  /** Minimum number of rows to display */
  minRows: PropTypes.number,
  /** Placeholder text when field is empty */
  placeholder: PropTypes.string,
  /** Whether the field is disabled */
  disabled: PropTypes.bool,
  /** Whether to auto focus the field */
  autoFocus: PropTypes.bool,
  /** Additional MUI sx styles to apply to the TextField */
  sx: PropTypes.object,
};

export default EditableTextField;
